// ──────────────────────────────────────────────────────────────────────────
// SpiegaPopover.js — Popover "Spiega questo numero" (D102 v129, D119 v130).
//
// Click su ⓘ → apre riquadrino con titolo, formula testuale, dettagli
// numerici reali calati sul caso. Click fuori chiude.
//
// Refactor v131 sub-deploy 3:
//   - Posizionamento intelligente: si apre a sinistra invece che a destra
//     se rischierebbe di uscire dal bordo della finestra (es. per ⓘ
//     sull'ultima colonna di una tabella).
//   - z-index: 1000 (era 100) per stare sopra a header sticky di tabelle.
//   - Stessa API e stessi stili dell'originale: retrocompatibile.
// ──────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useLayoutEffect } from "react";

export default function SpiegaPopover({ titolo, formula, dettagli }) {
  const [aperto, setAperto] = useState(false);
  const [aperturaSinistra, setAperturaSinistra] = useState(false);
  const [verticaleAlto, setVerticaleAlto] = useState(false);
  const refSpan = useRef(null);
  const refPopover = useRef(null);

  // Click fuori chiude
  useEffect(() => {
    if (!aperto) return;
    const handler = (e) => {
      if (refSpan.current && !refSpan.current.contains(e.target)) setAperto(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [aperto]);

  // Calcolo posizionamento intelligente alla apertura
  useLayoutEffect(() => {
    if (!aperto || !refSpan.current) return;
    const rectBtn = refSpan.current.getBoundingClientRect();
    const larghezzaPopover = 320; // costante interna
    const altezzaPopover = 130;   // stima (titolo + formula + dettagli)
    const margine = 16;

    // Decisione orizzontale
    // Predefinito: si apre verso sinistra (right:0 sul wrapper relative).
    // Se questo farebbe finire il popover fuori dal bordo sinistro della finestra,
    // commuto e lo apro verso destra.
    const xMinSeApreSinistra = rectBtn.right - larghezzaPopover;
    const apriSinistra = xMinSeApreSinistra >= margine;
    setAperturaSinistra(apriSinistra);

    // Decisione verticale
    // Predefinito: sotto. Se non c'è spazio sotto, lo apro sopra.
    const spazioSotto = window.innerHeight - rectBtn.bottom;
    const spazioSopra = rectBtn.top;
    const apriSopra = spazioSotto < altezzaPopover + margine && spazioSopra > spazioSotto;
    setVerticaleAlto(apriSopra);
  }, [aperto]);

  return (
    <span ref={refSpan} style={{ position: "relative", display: "inline-block", marginLeft: 6 }}>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-label="Spiega questo numero"
        title="Clicca per la spiegazione di questo numero"
        style={{
          width: 16, height: 16, borderRadius: "50%",
          background: aperto ? "#dbe7f5" : "#eef2f7",
          color: "#5a7596", border: "1px solid #cbd5e1",
          fontSize: 11, fontWeight: 600, lineHeight: "14px",
          cursor: "pointer", padding: 0, display: "inline-flex",
          alignItems: "center", justifyContent: "center",
        }}
      >i</button>
      {aperto && (
        <div
          ref={refPopover}
          style={{
            position: "absolute",
            // Verticale
            ...(verticaleAlto ? { bottom: 22 } : { top: 22 }),
            // Orizzontale
            ...(aperturaSinistra ? { right: 0 } : { left: 0 }),
            zIndex: 1000,
            width: 320, background: "#fff",
            border: "1px solid #d6dde6", borderRadius: 8,
            boxShadow: "0 8px 28px rgba(15, 23, 42, 0.18)",
            padding: "12px 14px", fontSize: 12, color: "#1f2a37",
            textAlign: "left", lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{titolo}</div>
          {formula && (
            <div style={{ color: "#4a5568", fontSize: 12, marginBottom: 6 }}>
              {formula}
            </div>
          )}
          {dettagli && (
            <div style={{ color: "#1f2a37", fontSize: 12 }}>
              {dettagli}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
